import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Download } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../lib/auth-store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button, ButtonLink } from '../ui/button'
import { EmptyState } from '../ui/empty-state'

const TH = 'h-10 px-2 text-left text-xs font-medium text-muted-foreground'

export function AuditLogSection({ accessToken }: { accessToken: string | null }) {
  const tenantRole = useAuthStore((s) => s.tenant?.role)
  const canExport = tenantRole === 'owner' || tenantRole === 'admin'
  const [entries, setEntries] = useState<Array<{
    id: string; action: string; resourceType: string | null; createdAt: string; ipAddress: string | null
  }> | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!accessToken || !expanded) return
    api.getAuditLog(accessToken).then((r) => setEntries(r.entries)).catch(() => {})
  }, [accessToken, expanded])

  // <a href> can't carry an Authorization header on a top-level
  // navigation, so the export endpoint accepts ?token=. Same pattern
  // as the project ZIP download. The token's already in localStorage;
  // putting it on this URL doesn't widen the trust surface.
  const exportUrl = accessToken
    ? `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/v1/workspace/audit-log/export?token=${encodeURIComponent(accessToken)}`
    : null

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left"
          aria-expanded={expanded}
        >
          <CardTitle>Audit log</CardTitle>
          <CardDescription>Recent workspace activity</CardDescription>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {canExport && exportUrl && (
            <ButtonLink
              variant="outline"
              size="sm"
              href={exportUrl}
              download
              onClick={(e) => e.stopPropagation()}
              title="Download the full audit log as CSV (SOC2/ISO export)"
            >
              <Download /> CSV
            </ButtonLink>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse audit log' : 'Expand audit log'}
          >
            {expanded ? <ChevronUp /> : <ChevronDown />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent>
          {!entries ? (
            <div className="space-y-2">
              <div className="dwlt-skeleton h-8" />
              <div className="dwlt-skeleton h-8" />
            </div>
          ) : entries.length === 0 ? (
            <EmptyState
              title="No activity yet"
              body="Actions like invites, key changes and deploys will show up here once your team starts working."
            />
          ) : (
            <div className="max-h-80 overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/30 backdrop-blur">
                  <tr>
                    <th className={TH}>Time</th>
                    <th className={TH}>Action</th>
                    <th className={TH}>Resource</th>
                    <th className={TH}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b last:border-b-0 hover:bg-muted/50">
                      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(e.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="p-2 font-mono text-xs">{e.action}</td>
                      <td className="p-2 text-xs text-muted-foreground">{e.resourceType ?? '—'}</td>
                      <td className="p-2 text-xs text-muted-foreground">{e.ipAddress ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
